// Kabukicho west edge (region kabuki-west): dense lantern-and-neon entertainment streets. Izakaya rows, hostess clubs,
// snack-bar strips with paper-lantern strings, and the back walls of the border buildings. Kinds are prefixed "kwest".
import { hash2 } from '../../common/src/util';
import { K, NEON, type NeonColor, px, speckle, dither, bands, disc, frame, shadow, dk, lt, alpha } from './hd-kit';
import { registerFront, registerRoof, registerRoofProp } from './hd-registry';
import { windowPane, door, lantern, vending, plant, bandSign, bladeSign, fitS, awning } from './hd-parts';
import type { G } from './hd-kit';
import type { LightFn } from './hd-registry';

// ---------------------------------------------------------------- roofs
/** tar roof with a lashed-down blue tarp and patched squares */
registerRoof('kwestTarp', { paint(g, x, y, w, h, seed) {
  px(g, x, y, w, h, K.tar);
  speckle(g, x, y, w, h, K.tarHi, 0.05, 41); speckle(g, x, y, w, h, K.tarLo, 0.06, 42);
  for (let k = 0; k < 2; k++) {
    const tw = 60 + Math.floor(hash2(seed, k) * 40), th = 36 + Math.floor(hash2(k, seed) * 24);
    const tx = x + 16 + Math.floor(hash2(seed + 3, k) * Math.max(1, w - tw - 40)), ty = y + 20 + Math.floor(hash2(k + 3, seed) * Math.max(1, h - th - 44));
    shadow(g, tx + 3, ty + 4, tw, th);
    px(g, tx, ty, tw, th, K.glassLo); px(g, tx + 1, ty + 1, tw - 2, th - 2, K.glass);
    for (let i = 8; i < tw; i += 12) px(g, tx + i, ty, 1, th, K.glassLo);
    px(g, tx + 2, ty + 2, tw - 4, 2, K.glassHi);
    for (const [cx, cy] of [[tx, ty], [tx + tw - 3, ty], [tx, ty + th - 3], [tx + tw - 3, ty + th - 3]]) px(g, cx, cy, 3, 3, K.ink);
  }
} });
/** concrete roof with gravel, patched slabs and a row of planter boxes */
registerRoof('kwestGravel', { paint(g, x, y, w, h, seed) {
  px(g, x, y, w, h, K.concreteLo);
  speckle(g, x, y, w, h, K.concrete, 0.35, 43); speckle(g, x, y, w, h, K.concreteHi, 0.04, 44);
  for (let k = 0; k < 3; k++) px(g, x + 12 + Math.floor(hash2(seed, k + 9) * Math.max(1, w - 80)), y + 18 + Math.floor(hash2(k + 9, seed) * Math.max(1, h - 60)), 52, 28, K.concrete);
  for (let i = 24; i + 16 < w - 20; i += 52) if (hash2(seed, i) < 0.5) { px(g, x + i, y + h - 26, 16, 8, K.ink); px(g, x + i + 1, y + h - 25, 14, 6, K.leafLo); px(g, x + i + 3, y + h - 28, 4, 4, K.leaf); px(g, x + i + 9, y + h - 28, 4, 4, K.leafHi); }
} });

// ---------------------------------------------------------------- roof props
/** a rooftop neon board seen from behind-and-above: frame, lit face and braces. p = [kind, x, y] */
registerRoofProp('kwestBoard', ({ g, x, y, L }) => {
  shadow(g, x + 5, y + 14, 56, 26);
  px(g, x, y, 56, 24, K.ink); px(g, x + 2, y + 2, 52, 20, dk(K.pink, 0.75)); frame(g, x + 4, y + 4, 48, 16, K.pink);
  for (let i = 8; i < 48; i += 8) px(g, x + i, y + 8, 4, 8, i % 16 ? lt(K.cyan, 0.5) : lt(K.pink, 0.55));
  px(g, x + 6, y + 24, 3, 8, K.metalLo); px(g, x + 46, y + 24, 3, 8, K.metalLo);
  L(x, y, 56, 24, K.pink, 0.5, true, true);
});
/** a stack of drain and gas pipes with elbows */
registerRoofProp('kwestPipes', ({ g, x, y }) => {
  shadow(g, x + 5, y + 6, 8, 44);
  for (let k = 0; k < 2; k++) { const px0 = x + k * 8; px(g, px0, y, 6, 44, K.ink); px(g, px0 + 1, y, 2, 44, K.metalHi); px(g, px0 + 3, y, 2, 44, K.metal); for (let j = 6; j < 44; j += 14) px(g, px0 - 1, y + j, 8, 3, K.metalLo); }
  px(g, x, y - 2, 16, 4, K.ink); px(g, x + 1, y - 1, 14, 2, K.metalHi);
});
/** a TV mast with cross bars and guy wires */
registerRoofProp('kwestMast', ({ g, x, y }) => {
  shadow(g, x + 6, y + 30, 22, 6, 0.3);
  px(g, x + 8, y, 2, 34, K.ink); px(g, x + 9, y, 1, 34, K.metalHi);
  for (const [j, wd] of [[2, 18], [9, 14], [16, 10]]) { px(g, x + 9 - wd / 2, y + j, wd, 2, K.ink); px(g, x + 9 - wd / 2 + 1, y + j, wd - 2, 1, K.metalHi); }
  px(g, x + 3, y + 33, 14, 3, K.ink); px(g, x + 4, y + 33, 12, 1, K.metal);
});

// ---------------------------------------------------------------- shared little parts
/** a string of paper lanterns hung across the wall */
function lanternString(g: G, x: number, y: number, w: number, L: LightFn, seed: number) {
  px(g, x, y, w, 1, K.ink);
  for (let i = 6; i < w - 6; i += 12) {
    const sag = Math.round(2 * Math.sin((i / w) * Math.PI)), col = hash2(i + seed, y) < 0.5 ? K.red : K.amber;
    px(g, x + i, y + sag, 1, 2, K.ink); px(g, x + i - 1, y + sag + 2, 4, 5, K.ink); px(g, x + i, y + sag + 3, 2, 3, col); px(g, x + i, y + sag + 3, 1, 1, lt(col, 0.6));
    L(x + i - 2, y + sag + 2, 6, 6, col, 0.45, true);
  }
}
/** a lit framed photo board of club hostesses (a head and shoulders silhouette) */
function photoBoard(g: G, x: number, y: number, col: NeonColor, L: LightFn) {
  const c = NEON[col];
  px(g, x, y, 22, 30, K.ink); px(g, x + 1, y + 1, 20, 28, dk(c, 0.7)); frame(g, x + 2, y + 2, 18, 26, dk(c, 0.3));
  disc(g, x + 11, y + 12, 4, 5, lt(c, 0.6)); px(g, x + 6, y + 18, 10, 8, lt(c, 0.35)); px(g, x + 8, y + 17, 6, 2, lt(c, 0.6));
  px(g, x + 6, y + 8, 10, 3, K.ink);
  L(x, y, 22, 30, c, 0.4, true);
}

// ---------------------------------------------------------------- fronts
/** Doc Neko's Chrome Clinic (the respawn and healing landmark by spawn): pale tiled wall, a lit cyan cross,
 *  wide glass doors onto the courtyard and a NEKO band sign */
registerFront('kwestClinic', { wall: K.plaster, wallAc: false }, ({ g, x, w, gf, gy, upper, y, L }) => {
  for (let j = y; j < gy; j += 8) px(g, x, j, w, 1, K.plasterLo); // tiled cladding
  if (upper >= 26) bandSign(g, x + w / 2, y + 10, 'クリニック', 'cyan', L, fitS('クリニック', w));
  // the cross: the clinic's mark, lit so it reads from across the street
  const cx = x + 18, cy = gy - 34;
  px(g, cx - 2, cy - 2, 28, 28, K.ink); px(g, cx + 7, cy, 6, 24, K.cyan); px(g, cx, cy + 9, 24, 6, K.cyan);
  px(g, cx + 8, cy + 1, 2, 22, lt(K.cyan, 0.6)); L(cx - 2, cy - 2, 28, 28, K.cyan, 0.5, true, true);
  // ground floor: a canopy and three wide glass doors with a bright, clean interior
  px(g, x, gy - 4, w, 4, K.ink); px(g, x + 1, gy - 4, w - 2, 2, K.cyan); L(x, gy - 4, w, 4, K.cyan, 0.3);
  const dw = Math.min(56, Math.floor((w - 24) / 3));
  for (let i = 0; i < 3; i++) {
    const dx = Math.round(x + (w - 3 * dw - 16) / 2 + i * (dw + 8));
    px(g, dx - 1, gy + 4, dw + 2, gf - 4, K.ink); bands(g, dx, gy + 5, dw, gf - 6, K.white, K.glassHi, 3);
    px(g, dx + dw / 2, gy + 5, 1, gf - 6, K.glassLo); px(g, dx + 3, gy + 8, 4, gf - 14, alpha(K.white, 0.5));
    L(dx, gy + 5, dw, gf - 6, K.cyan, 0.35, true);
  }
});
/** izakaya row: four lantern-hung bays with noren, lattice windows and beer crates */
registerFront('kwestIzakaya', { wall: K.wood }, ({ g, x, y, w, h, gf, gy, upper, seed, sign, L }) => {
  // shared kawara eave over the whole row
  px(g, x, gy - 10, w, 7, K.kawara); for (let i = 0; i < w; i += 6) px(g, x + i, gy - 10, 1, 7, K.kawaraLo); px(g, x, gy - 10, w, 1, K.kawaraHi); px(g, x, gy - 3, w, 3, alpha(K.night, 0.5));
  if (sign && upper >= 26) bandSign(g, x + w / 2, y + 12, sign.text, sign.color, L, fitS(sign.text, w));
  lanternString(g, x + 4, gy - 16, w - 8, L, seed);
  const n = Math.max(1, Math.floor(w / 96)), bw = w / n;
  for (let i = 0; i < n; i++) {
    const sx = Math.round(x + i * bw), dx = sx + Math.round(bw / 2) - 14;
    px(g, sx + 6, gy + 6, 22, gf - 14, K.ink); bands(g, sx + 8, gy + 8, 18, gf - 18, K.warmHi, K.warm, 3);
    for (let j = 8; j < gf - 10; j += 7) px(g, sx + 8, gy + j, 18, 1, K.woodLo); for (let k = 0; k < 18; k += 6) px(g, sx + 8 + k, gy + 8, 1, gf - 18, K.woodLo);
    L(sx + 8, gy + 8, 18, gf - 18, K.amber, 0.35, true);
    door(g, dx, gy + 6, 28, gf - 8, K.amber, L);
    const nc = i % 2 ? K.redLo : K.white;
    for (let k = 0; k < 2; k++) { px(g, dx + 1 + k * 13, gy + 6, 12, 15, nc); px(g, dx + 1 + k * 13, gy + 6, 12, 2, i % 2 ? K.redDk : K.plasterLo); px(g, dx + 6 + k * 13, gy + 8, 2, 10, alpha(K.night, 0.25)); }
    lantern(g, dx + 36, gy + 6, i % 2 ? K.amber : K.red, L);
    for (let k = 0; k < 2; k++) { px(g, sx + Math.round(bw) - 24 + k * 9, gy + gf - 14, 8, 10, K.ink); px(g, sx + Math.round(bw) - 23 + k * 9, gy + gf - 13, 6, 8, k ? dk(K.amber, 0.5) : dk(K.cyan, 0.55)); } // crates
    px(g, sx, gy + 4, 2, gf - 6, alpha(K.night, 0.35));
  }
});

/** hostess club: pink neon door in a cyan frame, blade signs and photo boards */
registerFront('kwestClub', { wall: dk(K.pink, 0.72), wallAc: true }, ({ g, x, y, w, h, gf, gy, upper, sign, L }) => {
  if (sign) {
    if (upper >= 26) bandSign(g, x + w / 2, y + 12, sign.text, sign.color, L, fitS(sign.text, w));
    else bladeSign(g, x + w - 26, gy - 4, sign.text, sign.color, L, 1);
  }
  bladeSign(g, x + 6, Math.max(y + 8, gy - 6), 'バー', 'cyan', L, 2);
  const cx = x + w / 2 - 20;
  px(g, cx - 12, gy - 3, 64, 5, K.ink); px(g, cx - 11, gy - 2, 62, 3, K.pink); px(g, cx - 11, gy + 1, 62, 2, alpha(K.night, 0.5)); L(cx - 12, gy - 3, 64, 5, K.pink, 0.5, true);
  door(g, cx, gy + 5, 40, gf - 7, K.pink, L); frame(g, cx - 3, gy + 2, 46, gf - 2, K.cyan); L(cx - 3, gy + 2, 46, gf - 2, K.cyan, 0.25);
  photoBoard(g, x + 46, gy + 8, 'pink', L);
  if (w > 220) { photoBoard(g, x + w - 70, gy + 8, 'cyan', L); photoBoard(g, x + w - 44, gy + 8, 'pink', L); } else photoBoard(g, x + w - 38, gy + 8, 'cyan', L);
  px(g, x + 6, gy + gf - 14, 10, 12, K.ink); px(g, x + 7, gy + gf - 13, 8, 10, dk(K.pink, 0.5)); // sandwich board
});

/** snack-bar strip: many small doors, each with its own tiny neon sign, and a paper-lantern string */
registerFront('kwestSnack', { wall: K.wallLo }, ({ g, x, y, w, h, gf, gy, upper, seed, sign, L }) => {
  lanternString(g, x + 2, gy - 12, w - 4, L, seed + 5);
  const n = Math.max(1, Math.floor((w - 8) / 44));
  for (let i = 0; i < n; i++) {
    const sx = x + 6 + Math.round(i * (w - 12) / n), col: NeonColor = i % 3 === 0 ? 'pink' : i % 3 === 1 ? 'cyan' : 'amber';
    px(g, sx, gy - 4, 28, 9, K.ink); px(g, sx + 1, gy - 3, 26, 7, dk(NEON[col], 0.7)); px(g, sx + 4, gy - 1, 20, 2, lt(NEON[col], 0.55)); L(sx, gy - 4, 28, 9, NEON[col], 0.5, true, true);
    door(g, sx + 4, gy + 6, 20, gf - 8, NEON[col], L);
    px(g, sx + 26, gy + 8, 2, gf - 12, alpha(K.night, 0.4));
    if (hash2(sx, seed) < 0.5) plant(g, sx + 28, gy + gf - 20);
  }
  if (sign && upper >= 26) bandSign(g, x + w / 2, y + 12, sign.text, sign.color, L, fitS(sign.text, w));
});

/** back wall of a border building: service door, caged lamp, drain pipes, wall units and tags */
registerFront('kwestBack', { wall: K.wallLo, wallAc: true }, ({ g, x, y, w, h, gf, gy, upper, seed, sign, L }) => {
  for (let i = 18; i < w - 10; i += 56 + Math.floor(hash2(i, seed) * 20)) { // drain pipes
    px(g, x + i, y + 8, 4, h - 12, K.ink); px(g, x + i + 1, y + 8, 1, h - 12, K.metalHi); px(g, x + i + 2, y + 8, 1, h - 12, K.metal);
    for (let j = y + 18; j < y + h - 10; j += 22) px(g, x + i - 1, j, 6, 2, K.metalLo);
  }
  const n = Math.max(1, Math.floor(w / 64));
  for (let i = 0; i < n; i++) {
    const dx = x + Math.round((i + 0.5) * w / n) - 10;
    px(g, dx - 2, gy + 6, 24, gf - 7, K.ink); px(g, dx, gy + 8, 20, gf - 9, i % 2 ? K.metal : K.metalLo); px(g, dx + 15, gy + 22, 2, 4, K.metalHi);
    px(g, dx + 4, gy + 1, 12, 4, K.ink); px(g, dx + 5, gy + 2, 10, 2, K.warmHi); L(dx - 2, gy - 1, 24, 10, K.amber, 0.4, true);
    if (i % 2) { px(g, dx + 26, gy + gf - 18, 10, 14, K.ink); px(g, dx + 27, gy + gf - 17, 8, 12, dk(K.cyan, 0.55)); }
    else vending(g, dx + 26, gy + gf - 40, i % 4 ? K.white : dk(K.pink, 0.4), L);
  }
  px(g, x + 8, gy + 14, 14, 5, alpha(K.pink, 0.4)); px(g, x + w - 26, gy + 20, 16, 4, alpha(K.cyan, 0.35)); // tags
  if (sign && upper >= 26) bladeSign(g, x + w - 24, y + 12, sign.text, sign.color, L, 2);
});
